/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import Breadcrumbs from '@atlaskit/breadcrumbs/breadcrumbs';
import { BreadcrumbsCurrentItem } from '@atlaskit/breadcrumbs/breadcrumbs-current-item';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs/breadcrumbs-item';
import { BreadcrumbsSkeleton } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton';
import { BreadcrumbsSkeletonItem } from '@atlaskit/breadcrumbs/breadcrumbs-skeleton-item';
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { HUB_FEATURES, useIsHubFeatureEnabled } from '../../controllers/use-is-hub-feature-enabled';
import { type HubBreadcrumbItem, useHubBreadcrumbItems } from './hub-breadcrumbs-context';
import { messages } from './messages';

const styles = cssMap({
	container: {
		boxSizing: 'border-box',
		minWidth: 0,
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		width: '100%',
	},
	item: {
		display: 'flex',
		boxSizing: 'border-box',
		minWidth: 0,
		maxWidth: '100%',
		alignItems: 'center',
		alignSelf: 'center',
		flexDirection: 'row',
		font: token('font.body'),
		marginTop: 0,
	},
	separator: {
		width: '8px',
		flexShrink: 0,
		paddingBlock: token('space.025'),
		paddingInline: token('space.100'),
		textAlign: 'center',
	},
	itemText: {
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const StaticHubParentBreadcrumb = ({ item }: { item: HubBreadcrumbItem }): JSX.Element => {
	return (
		<Box xcss={styles.item} as="li">
			<span css={styles.itemText}>
				<Text color={'color.text.subtlest'} maxLines={1}>
					{item.text}
				</Text>
			</span>
			<span aria-hidden css={styles.separator}>
				<Text color="color.text.subtlest">/</Text>
			</span>
		</Box>
	);
};

const StaticHubCurrentBreadcrumb = ({ item }: { item: HubBreadcrumbItem }): JSX.Element => {
	return (
		<Box xcss={styles.item} as="li">
			<span aria-current="page" css={styles.itemText}>
				<Text maxLines={1}>{item.text}</Text>
			</span>
		</Box>
	);
};

export const HubBreadcrumbsSkeleton = ({
	itemCount,
}: {
	itemCount: number;
}): JSX.Element | null => {
	const [isBreadcrumbsEnabled] = useIsHubFeatureEnabled([HUB_FEATURES.BREADCRUMBS]);

	if (!isBreadcrumbsEnabled) {
		return null;
	}

	return (
		<div css={styles.container}>
			<BreadcrumbsSkeleton>
				{Array.from({ length: itemCount }).map((_, index) => (
					<BreadcrumbsSkeletonItem width={60} key={index} />
				))}
			</BreadcrumbsSkeleton>
		</div>
	);
};

export const HubBreadcrumbs = (): JSX.Element | null => {
	const intl = useIntl();
	const items = useHubBreadcrumbItems();
	const currentItem = items[items.length - 1];

	if (!currentItem) {
		return null;
	}

	const label = intl.formatMessage(messages.label);
	const ellipsisLabel = intl.formatMessage(messages.showMore);

	return (
		<div css={styles.container} data-testid="hub-breadcrumbs-container">
			<Breadcrumbs
				ellipsisLabel={ellipsisLabel}
				label={label}
				size="medium"
				testId="hub-breadcrumbs"
			>
				{items
					.slice(0, -1)
					.map((item) =>
						item.href ? (
							<BreadcrumbsItem href={item.href} key={item.id} text={item.text} />
						) : (
							<StaticHubParentBreadcrumb item={item} key={item.id} />
						),
					)}
				{currentItem.href ? (
					<BreadcrumbsCurrentItem href={currentItem.href} text={currentItem.text} />
				) : (
					<StaticHubCurrentBreadcrumb item={currentItem} />
				)}
			</Breadcrumbs>
		</div>
	);
};
